import { alertKey } from '../components/wiseguy/alertStore';
import type { WiseguyAlert } from '../types/wiseguy';

/**
 * How many of the day's largest single-name alerts the Market Overview panel
 * shows. Ranked after the full session is fetched, never before.
 */
export const MARKET_TOP_FLOW_ROWS = 12;

/**
 * Session fetch cap. Same number as `SESSION_LIMIT` in `useWiseguyAlerts` —
 * sized off the busiest floored QF day (~680), not the unfloored Django mirror.
 * Ranking a shorter page would mean "largest of the most recent few" under a
 * heading that says Largest.
 */
export const MARKET_TOP_FLOW_LIMIT = 1000;

/**
 * Rank the day's QF alerts for the Market Overview panel.
 *
 * ⚠️ **ETF rows are dropped, missing `asset_class` is kept.** SPY and QQQ
 * print size every session and would fill this panel most days. Compared
 * against `'ETF'` rather than for `'Stock'` so a provider gap does not drop a
 * real single-name print. Index names stay — they are not the ETFs this
 * filter exists to exclude.
 *
 * ⚠️ **Rank the whole session, then slice.** The alert route has no `sort`.
 */
export function rankLargestFlowToday(
  alerts: WiseguyAlert[],
  limit: number = MARKET_TOP_FLOW_ROWS
): WiseguyAlert[] {
  return alerts
    .filter((a) => a.asset_class !== 'ETF')
    .slice()
    .sort((a, b) => (Number(b.bet) || 0) - (Number(a.bet) || 0))
    .slice(0, limit);
}

/**
 * React key / identity. `alert.id` is an FNV-1a hash that collides at real
 * volume — `external_id` is the QF identity.
 */
export function flowRowKey(alert: WiseguyAlert): string {
  return alertKey(alert);
}

/**
 * Strike as `150.00`. Non-numeric / missing renders a dash, never `"NaN"`.
 */
export function fmtStrike(v: string | number | null | undefined): string {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? '—' : n.toFixed(2);
}
